import { ExternalLink } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useState } from 'react';
import { Calendar } from 'lucide-react';
import axios from 'axios';
import { QuizrrStepper } from './Stepper/QuizrrStepper';
import ProgressAnalysis from './ProgressAnalysis/ProgressAnalysis';
import AllTestsList from './AllTestsList/AllTestsList';
import Resources from './Resources/Resources';
import { Badge } from '../components/ui/badge';
import { TestSchedule } from './Calender/TestSchedule';
import DashboardChart from './DashboardChart/DashboardChart';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/selectDashboard';
import { useLocation, useNavigate } from 'react-router-dom';
import TopHeader from '../Layout/TopHeader';
import { OnboardingModal } from '../Auth/AuthOnboarding/OnboardingModal';
import { HeartIcon, WarningIcon } from '@phosphor-icons/react';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';
import Banner from '../components/generic/Banner';
import NoTestSeries from '../TestSeries/NoTestSeriesBought/NoTestSeries';
import BannerDesktop from '../../../../apps/frontend/public/img/banner-desktop.webp';
import BannerMobile from '../../../../apps/frontend/public/img/banner-mobile.webp';
import ExploreSection from './ExploreSection/ExploreSection';
import ExploreTestSeries from './ExploreTestSeries/ExploreTestSeries';
import usePromotionPacks from './ExploreTestSeries/usePromotionPacks';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import NotAccessibleModal from '../components/modals/NotAccessibleModal';

const useQuery = () => new URLSearchParams(useLocation().search);

/** Same drift threshold as the AYJR system health check page (5 minutes). */
const SYSTEM_CLOCK_DRIFT_MS = 300_000;

function getTimezoneCheckUrl() {
  const base =
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL
      ? String(import.meta.env.VITE_API_URL).replace(/\/$/, '')
      : '';
  return base
    ? `${base}/api/content/timezone/?timeZone=Asia/Kolkata`
    : 'https://ayjr-api.quizrr.in/api/content/timezone/?timeZone=Asia/Kolkata';
}

/** Mirrors the fallback in the static health-check page when the API is unreachable. */
function getFallbackClockWarningMessage() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 5.5 * 3600000);
  const diff = Math.abs(ist - now);
  if (diff <= 600000) return null;
  return 'Your system clock may be incorrect. Please set your device to Indian Standard Time (IST) so tests start and end at the correct times.';
}

function Dashboard() {
  const query = useQuery();
  const utmSource = query.get('utm_source');
  const utmMedium = query.get('utm_medium');
  const utmCampaign = query.get('utm_campaign');
  const { purchasedPacks, purchasedFreePacksCollective, freePacks, selectedPack, handlePackChange } = usePackStorage();
  const { packs: promotionPacks } = usePromotionPacks();

  const [isExpanded, setIsExpanded] = useState(false);
  const [platformData, setPlatformData] = useState({});
  const [importantInfo, setImportantInfo] = useState('');
  const [userProfile, setUserProfile] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('userProfile'));
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isHelloBarVisible, setIsHelloBarVisible] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [systemClockWarning, setSystemClockWarning] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if HelloBar was previously closed
    const isClosed = sessionStorage.getItem('hellobarClosed');
    if (isClosed) {
      setIsHelloBarVisible(false);
    }

    // check for not accessible modal
    const showNotAccessibleModal = Boolean(query.get('showNotAccessibleModal'));
    setShowModal(showNotAccessibleModal);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkSystemClock() {
      const url = getTimezoneCheckUrl();
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 8000);

      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error('timezone request failed');
        const data = await res.json();
        const serverTime = new Date(data.currentTime);
        const localTime = new Date();
        const drift = Math.abs(serverTime - localTime);
        if (cancelled) return;
        if (drift > SYSTEM_CLOCK_DRIFT_MS) {
          const offBy = Math.round(drift / 60000);
          setSystemClockWarning(
            `Your device clock is off by about ${offBy} minute${offBy === 1 ? '' : 's'} compared to Indian Standard Time. Please correct your date and time settings.`,
          );
        }
      } catch {
        if (cancelled) return;
        const fallback = getFallbackClockWarningMessage();
        if (fallback) setSystemClockWarning(fallback);
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    checkSystemClock();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleHelloBarClose = () => {
    setIsHelloBarVisible(false);
  };

  const savePackIdToStorage = (packId) => {
    try {
      localStorage.setItem('selectedPackId', packId);
    } catch (error) {
      console.error('Error saving pack ID to localStorage:', error);
    }
  };

  const dashboardScrollRef = useRef(null);

  useEffect(() => {
    const fetchPacks = async () => {
      const apiUrl = `/api/packs/v3`;
      try {
        setIsLoading(true);
        const response = await axios.get(apiUrl);
        const packs = response?.data?.data?.purchasedPacks;
        const notPurchasedPacks = response?.data?.data?.notPurchasedPacks;

        if (!packs || !packs?.length) {
          savePackIdToStorage(notPurchasedPacks[0]._id);
          setSelectedPack(notPurchasedPacks[0]);
        } else return;

        if (packs?.length > 0) {
          const savedPackId = localStorage.getItem('selectedPackId');
          if (savedPackId && packs?.length > 0) {
            const savedPack = packs.find((pack) => pack._id === savedPackId);
            if (savedPack) {
              setSelectedPack(savedPack);
            } else {
              savePackIdToStorage(packs[0]._id);
              setSelectedPack(packs[0]);
            }
          } else {
            savePackIdToStorage(packs[0]._id);
            setSelectedPack(packs[0]);
          }
        }
      } catch (error) {
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
          console.error('Error response headers:', error.response.headers);
        } else if (error.request) {
          console.error('Error request:', error.request);
        } else {
          console.error('Error message:', error.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // fetchPacks();
  }, [navigate]);

  useEffect(() => {
    async function checkOnboardingStatus() {
      try {
        const res = await axios.get('/api/auth/user');
        const user = res?.data?.data;
        const hasCompletedOnboarding = user?.isOnboardingV2Done;
        if (!hasCompletedOnboarding) {
          setShowOnboarding(true);
        }
      } catch (error) {
        const params = new URLSearchParams();

        if (utmSource) params.append('utm_source', utmSource);
        if (utmMedium) params.append('utm_medium', utmMedium);
        if (utmCampaign) params.append('utm_campaign', utmCampaign);

        const queryString = params.toString();
        navigate(`/auth/login${queryString ? `?${queryString}` : ''}`);
      }
    }
    checkOnboardingStatus();
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const [isInitialBounce, setIsInitialBounce] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialBounce(false);
    }, 4500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    axios
      .get('/api/platform')
      .then((response) => {
        const data = response.data.data;
        setPlatformData(data);
        const html = data.importantInfo;
        const examDate = new Date(data.examDate).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
        const resultDate = new Date(data.detailAccessDate).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
        const localUser = JSON.parse(localStorage.getItem('userProfile'));
        let updatedHtml = html
          .replaceAll('{examDate}', examDate)
          .replaceAll('{resultDate}', resultDate)
          .replaceAll('{packName}', data.packName)
          .replaceAll('{mobileNumber}', localUser.mobileNumber);

        setImportantInfo(updatedHtml);
      })
      .catch(() => {});
  }, []);

  function ImportantInfo({ platformData }) {
    const containerRef = useRef(null);

    useEffect(() => {
      if (containerRef.current && platformData?.importantInfo) {
        // Clear previous content
        containerRef.current.innerHTML = platformData.importantInfo;

        // Find and reload iframes to ensure they load
        const iframes = containerRef.current.querySelectorAll('iframe');
        iframes.forEach((iframe) => {
          const src = iframe.getAttribute('src');
          iframe.setAttribute('src', ''); // Reset
          iframe.setAttribute('src', src); // Reload
        });
      }
    }, [platformData?.importantInfo]);

    return <div ref={containerRef} />;
  }

  return (
    <AppLayout
      purchasedPacks={purchasedFreePacksCollective}
      selectedPack={selectedPack}
      onPackChange={handlePackChange}
    >
      {/* Full-width scrollable container */}
      <div
        ref={dashboardScrollRef}
        className={`
        h-screen overflow-y-auto 
        transition-all duration-700 ease-in-out 
        flex justify-center
      `}
        style={{
          width: '100%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Inner fixed-width content container */}
        <div
          className={`
          flex flex-col py-4
          transition-all duration-700 ease-in-out
          w-full max-w-[1090px]
          pt-8
        `}
        >
          {/* <div
          className={`
            ${!isExpanded ? '' : '-mx-4 md:-mx-4 -my-4'}
            ${isHelloBarVisible ? 'mb-8' : ''}
            lg:mb-0
          `}
        >
          <HelloBar isCollapsed={!isExpanded} onClose={handleHelloBarClose} />
        </div> */}

          <div className="">
            <Banner />
          </div>

          {systemClockWarning && (
            <div
              role="alert"
              className="mx-4 md:mx-2 mb-4 rounded-lg border border-[var(--q3-danger-normal)] bg-[var(--q3-surface-default)] px-4 py-3 text-sm text-[var(--q3-neutral-default)] shadow-sm"
            >
              <div className="flex gap-3 items-start">
                <WarningIcon
                  className="flex-shrink-0 mt-0.5 text-[var(--q3-danger-normal)]"
                  size={22}
                  weight="fill"
                  aria-hidden
                />
                <p className="leading-relaxed">{systemClockWarning}</p>
              </div>
            </div>
          )}

          <TopHeader />

          <div className="relative mb-8 mt-2 md:mt-0 flex-shrink-0 px-4 md:px-2 flex justify-between w-full items-center">
            {
              <Select value={selectedPack?._id} onValueChange={handlePackChange}>
                <SelectTrigger
                  size="lg"
                  className="min-w-[120px] max-w-[90vw] w-fit !bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] cursor-pointer rounded-4xl shadow-none py-1 lg:py-2"
                >
                  <SelectValue placeholder={<div className="font-semibold">Select Test</div>} />
                </SelectTrigger>

                <SelectContent className="rounded-xl bg-[var(--q3-surface-default)] shadow-lg ">
                  <SelectGroup>
                    {purchasedFreePacksCollective?.map((each) => {
                      const isSelected = selectedPack?._id === each._id;
                      return (
                        <SelectItem
                          className={`px-4 py-4 font-semibold cursor-pointer w-full ${
                            isSelected
                              ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                              : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
                          }`}
                          key={each._id}
                          value={each?._id}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <img alt="img" src={each?.icon || '/placeholder.svg'} className="h-5 w-5 md:h-6 md:w-6" />
                            <p className="font-medium whitespace-nowrap" style={{ fontSize: '16px' }}>
                              <span className="hidden sm:inline">{each?.title}</span>
                              <span className="inline sm:hidden">
                                {each?.shortTitle || each?.exam?.title || each?.title}
                              </span>
                            </p>
                          </div>
                        </SelectItem>
                      );
                    })}
                    <SelectItem
                      value="view-other-tests"
                      className="px-4 py-4 cursor-pointer text-[var(--q3-neutral-default)] w-full"
                    >
                      <div className="flex items-center justify-between w-full gap-3">
                        <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
                          View Other Test Series
                        </span>
                        <ExternalLink className="w-4 h-4 text-[var(--q3-neutral-default)] flex-shrink-0" />
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            }
          </div>

          <div className="mb-30">
            <div>
              <AllTestsList packId={selectedPack?._id} />
            </div>

            {/* Important Info Section */}
            {platformData && platformData?.showImportantInfo && (
              <div className="my-16 px-4 md:px-2">
                <p className="text-2xl font-bold text-[var(--q3-neutral-default)]">Important Info</p>
                <div
                  className="text-[var(--q3-neutral-default)] mt-2"
                  dangerouslySetInnerHTML={{
                    __html: importantInfo || '',
                  }}
                />
              </div>
            )}
            {/* {promotionPacks && promotionPacks?.length > 0 && (
              <div className="mt-10">
                <ExploreTestSeries />
              </div>
            )} */}

            <div className="text-[28px] md:text-[40px] text-[var(--q3-neutral-light-v3)] font-extrabold p-2 mt-10 mb-24 md:mb-0 leading-snug text-start">
              <p className="text-wrap">
                Made with&nbsp;
                <HeartIcon
                  weight="fill"
                  className={`inline-block w-8 h-8 align-middle text-[var(--q3-danger-normal)] throb-on-hover ${
                    isInitialBounce ? 'throb-animation' : ''
                  }`}
                />
                &nbsp;by the Quizrr team (Powered by MathonGo), just for aspirants like you.
              </p>
              <p className="mt-4 text-3xl font-bold opacity-35" style={{ fontFamily: "'Caveat', cursive" }}>
                #PaperPhodnaHai
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Modal */}
      <OnboardingModal open={showOnboarding} onComplete={handleOnboardingComplete} />
      {/* Not Accessible Modal */}
      {showModal && (
        <NotAccessibleModal
          onClose={() => {
            setShowModal(false);
            // remove query param from url
            const url = new URL(window.location.href);
            url.searchParams.delete('showNotAccessibleModal');
            window.history.replaceState({}, '', url);
          }}
          platformData={platformData}
        />
      )}
    </AppLayout>
  );
}

export default Dashboard;
